import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import dayjs from "dayjs";

import { Typography } from "@mui/material";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Button from "src/components/core/Button";

import font from "src/styles/font";
import { IconTicket } from "src/icons";
import { DarkGrey, White01 } from "src/styles/color";
import { useSnackbar } from "notistack";
import { createPromoCode, updatePromoCode } from "src/api/promoCode";
import PromoCodeItem from "src/components/promo-code/PromoCodeItem";

const ConditionBox = styled.div`
  width: 100%;
  margin-top: 24px;
  padding: 32px 24px;
  background: ${White01};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ul {
    margin: 0;
    padding-left: 24px;
  }
  li {
    margin: 0;
    font-size: ${font.size.md};
    font-weight: 400;
    line-height: 1.5;
    color: ${DarkGrey};
  }
`;

const Dialog = muiStyled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 32,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    // paddingBottom: 0,
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
}));

const PreviewDialog = ({
  previewData,
  onClose,
  onSuccess,
  isDetail = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async () => {
    try {
      const promoCodeParams = {
        ...previewData,
        product_choice_promocodes: previewData?.product_choice_promocodes?.map(
          (product) => {
            return product._id;
          }
        ),
      };
      // console.log("------promoCodeParams-----", promoCodeParams);

      !!promoCodeParams._id
        ? await updatePromoCode(promoCodeParams)
        : await createPromoCode(promoCodeParams);

      enqueueSnackbar(
        `${!!promoCodeParams._id ? "Update" : "Create"} Success!`,
        {
          variant: "success",
        }
      );

      await onSuccess();
    } catch (err) {
      console.error("err:", err.response);
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: 740,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          textAlign: "center",
        }}
      >
        <IconTicket />
        รายละเอียดการใช้ส่วนลด
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            borderRadius: "50%",
            border: "1px solid var(--lightgrey-color)",
            position: "absolute",
            right: 16,
            top: 16,
            color: "var(--primary-color)",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 5,
          pb: isDetail ? 4 : 0,
          width: "100%",
          position: "relative",
          "& .MuiBox-root": {
            // maxWidth: "530px",
            mx: "auto",
          },
        }}
      >
        <PromoCodeItem
          promoCode={previewData?.promo_code}
          promoName={previewData?.promo_name}
          promoTitle={previewData?.promo_title}
          promoExpire={previewData?.promo_expire_time}
        />

        <ConditionBox>
          <Typography variant="subtitle1">เงื่อนไขส่วนลด</Typography>

          <div>
            <Typography variant="subtitle2">ระยะเวลาที่ใช้ได้</Typography>
            <Typography variant="body2" gutterBottom>
              {`${dayjs(previewData?.promo_start_time).format(
                `DD MMMM YYYY HH:mm`
              )} - ${dayjs(previewData?.promo_expire_time).format(
                `DD MMMM YYYY HH:mm`
              )}`}
            </Typography>
          </div>

          <div>
            <Typography variant="subtitle2">โค้ด</Typography>
            <Typography variant="body2" gutterBottom>
              {previewData?.promo_code || `รหัสโค้ดส่วนลด`}
            </Typography>
          </div>

          {!!previewData?.promo_product_group && (
            <div>
              <Typography variant="subtitle2">กลุ่มสินค้า</Typography>
              <Typography variant="body2" gutterBottom>
                {previewData?.promo_product_group === "ready"
                  ? "สินค้าพร้อมส่ง"
                  : previewData?.promo_product_group === "pre-order"
                    ? "สินค้าพรีออเดอร์"
                    : "สินค้าทุกกลุ่ม"}
              </Typography>
            </div>
          )}

          <div>
            <Typography variant="subtitle2">การชำระเงิน</Typography>
            <Typography variant="body2" gutterBottom>
              {previewData?.promo_pay_type === "promptpay"
                ? "ชำระผ่านพร้อมเพย์/โอนผ่านบัญชีธนาคาร"
                : previewData?.promo_pay_type === "credit-card"
                  ? "ชำระผ่านบัตรเครดิต"
                  : previewData?.promo_pay_type === "installment"
                    ? "ผ่อนชำระ"
                    : "ทุกการชำระเงิน"}
            </Typography>
          </div>

          {!!previewData?.product_choice_promocodes && (
            <div>
              <Typography variant="subtitle2">
                จำนวนสินค้าที่ร่วมรายการ
              </Typography>
              <Typography variant="body2" gutterBottom>
                {`${previewData?.product_choice_promocodes?.length} รายการ`}
              </Typography>
            </div>
          )}

          <div>
            <Typography variant="subtitle2">รายละเอียดเพิ่มเติม</Typography>
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: previewData?.promo_description,
              }}
            />
          </div>
        </ConditionBox>
      </DialogContent>

      {!isDetail && (
        <DialogActions sx={{ px: 3, py: 4 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => onClose()}
            sx={{
              width: "100%",
              maxWidth: 140,
              borderRadius: 4,
            }}
          >
            แก้ไข
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={() => onSubmit()}
            sx={{
              width: "100%",
              maxWidth: 140,
              borderRadius: 4,
            }}
          >
            บันทึก
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PreviewDialog;
